import { Component, OnInit } from '@angular/core';

import { Utils } from '../../../../../common/js/Utils';
import { AmorphicService } from '../../../services/amorphic/amorphic.service';
import { ModalService } from '../../../services/modal/modal.service';
import { TimeFrequency } from '../../../../../common/js/static/Product';
import { BaseModalComponent } from '../../modals/base-modal.component';
import { ProductQuoteResponse } from '../../../../js/interfaces/CoverpathTypes';
import { PremiumRequest } from '../../../../../common/js/productService/ProductServiceClient';
import { AccountCenterService } from '../../../services/account-center/account-center.service';
import { PolicyData } from '../../../interfaces/PolicyData';

type FlatExtraDisplayRow = {
  displayText: string;
  toolTip: string;
  amount: string;
};

@Component({
  selector: 'premium-breakdown-modal',
  moduleId: module.id,
  templateUrl: './premium-breakdown-modal.component.html',
})
export class PremiumBreakdownModalComponent extends BaseModalComponent implements OnInit {
  base: string;
  waiver: string;
  flatExtras: Array<FlatExtraDisplayRow> = [];
  total: string;
  timeFrequency: string;
  data;
  basePremiumGIR: string;
  basePremiumRTR: string;
  basePremiumALIR: string;
  basePremiumCIR: string;
  isART: boolean;
  premiumIncrease: string;
  waiverIncrease: string;
  waiverIncreased: boolean;
  increaseDate: Date;
  accountCenterService: AccountCenterService;

  cancel() {}
  confirm() {}

  constructor(
    private amorphicService: AmorphicService,
    protected modalService: ModalService,
    accountCenterService: AccountCenterService
  ) {
    super(modalService);
    this.accountCenterService = accountCenterService;
  }

  ngOnInit() {
    this.initializeData();
  }

  initializeData() {
    const isPrimaryPolicy = this.data.modalData && this.data.modalData.isPrimaryPolicy;
    const primaryPolicyId =
      this.amorphicService.controller.customer.application.primaryPolicyWithPendingAmendment.__id__;
    const primaryPolicyData = this.accountCenterService.getPolicyDataFromPolicy(primaryPolicyId as string);
    const secondaryPolicyId = primaryPolicyData.getCACIsJointApp()
      ? this.amorphicService.controller.customer.application.secondaryPolicyWithPendingAmendment.__id__
      : '';

    const customerPolicyCACData = isPrimaryPolicy
      ? primaryPolicyData
      : this.accountCenterService.getPolicyDataFromPolicy(secondaryPolicyId);
    const customerCACPaymentData = customerPolicyCACData.getCACPaymentData();

    const paymentFrequency = primaryPolicyData.getCACIsJointApp()
      ? primaryPolicyData.getCACPaymentData().getCACPaymentFrequency()
      : customerPolicyCACData.getCACPaymentData().getCACPaymentFrequency();

    const per = paymentFrequency !== 'semi-annual' ? `/` : ' ';
    this.timeFrequency = `${per}${TimeFrequency[paymentFrequency]}`;
    this.base = `${Utils.formatCurrency(customerCACPaymentData.getCACBasePremium(), 2)}`;

    if (customerCACPaymentData.getCACHasWaiver()) {
      this.waiver = `${Utils.formatCurrency(customerCACPaymentData.getCACTotalWaiverPremium(), 2)}`;
    }

    if (customerCACPaymentData.getCACHasGirRider()) {
      this.basePremiumGIR = `${Utils.formatCurrency(customerCACPaymentData.getCACgirRider().premium, 2)}`;
    }

    if (customerCACPaymentData.getCACHasRtrRider()) {
      this.basePremiumRTR = `${Utils.formatCurrency(customerCACPaymentData.getCACrtrRider().premium, 2)}`;
    }

    if (customerCACPaymentData.getCACHasAlirRider()) {
      this.basePremiumALIR = `${Utils.formatCurrency(customerCACPaymentData.getCACAlirRider().premium, 2)}`;
    }

    if (customerCACPaymentData.getCACCirRider()) {
      this.basePremiumCIR = `${Utils.formatCurrency(customerCACPaymentData.getCACCirRider().premium, 2)}`;
    }

    const cacFlatExtra = customerCACPaymentData.getCACAllAggregateFlatExtras();

    for (const flatExtra of cacFlatExtra) {
      const displayInfo = customerCACPaymentData.getCACDisplayInfo(flatExtra);

      this.flatExtras.push({
        displayText: displayInfo.name,
        toolTip: displayInfo.toolTip,
        amount: `${Utils.formatCurrency(flatExtra.premium, 2)}`,
      });
    }

    this.total = `${Utils.formatCurrency(customerCACPaymentData.getCACTotalPremium(), 2)}`;

    if (customerPolicyCACData.getCACIsAnnuallyRenewableTerm()) {
      this.fetchProductInfo(customerPolicyCACData, primaryPolicyData).then((response) => {
        this.increaseDate = customerPolicyCACData.getCACIssuedDate();
        if (this.increaseDate) {
          this.increaseDate.setFullYear(this.increaseDate.getFullYear() + 1);
        }
        this.isART = true;
        this.premiumIncrease = response.annualBaseRates[1].toFixed(2);
        this.waiverIncrease = response.annualWaiverRates[1].toFixed(2);
        this.waiverIncreased = response.annualWaiverRates[0] < response.annualWaiverRates[1];
      });
    }
  }

  //Note - we need to use the primary policy person data as secondary policy owner and insured will be empty
  fetchProductInfo(customerPolicyCACData: PolicyData, primaryPolicyData: PolicyData): Promise<ProductQuoteResponse> {
    const request: PremiumRequest = {
      dob: primaryPolicyData.getCACInsuredDOB(),
      product: customerPolicyCACData.getCACProduct(),
      productType: customerPolicyCACData.getCACProductType(),
      effectiveDate: new Date(),
      gender: primaryPolicyData.getCACInsuredGender(),
      ratingCode: customerPolicyCACData.getCACRatingCode(),
      state: primaryPolicyData.getCACOwnerPersonResidentialAddressState(),
      term: 1,
      face: customerPolicyCACData.getCACPaymentData().getCACFaceAmount(),
      flatExtras: [],
      paymentFrequency: primaryPolicyData.getCACPaymentData().getCACPaymentFrequency(),
      waiver: customerPolicyCACData.getCACPaymentData().getCACHasWaiver(),
      asOfDate: new Date(),
    };

    return this.amorphicService.controller.publicFetchProductQuote(
      request,
      null,
      customerPolicyCACData.getCACProductPremiumVersion()
    );
  }

  closeModal(): void {
    this.modalService.deactivate();
  }
}
